/**
 * Interlinking Service (Spider Net Engine)
 * 
 * Dynamically builds a web of internal links between articles to enhance SEO dwell time.
 * Replaces hardcoded DEEP_LINKING_MAP with smart logic.
 */

// Weights for relevance scoring
const SCORE = {
    SAME_TAG: 3,
    SAME_CATEGORY: 2,
    PILLAR_BONUS: 5,
    TITLE_MATCH: 1
};

/**
 * Generates specific anchor text for a target article
 * Uses the first SEO keyword if available, otherwise optimized title.
 */
const getAnchorText = (targetArticle) => {
    if (targetArticle.seoKeywords && targetArticle.seoKeywords.length > 0) {
        // capitalization
        return targetArticle.seoKeywords[0].replace(/\b\w/g, c => c.toUpperCase());
    }
    // Fallback: title truncated or simplified could go here, but full title is safer for now
    return targetArticle.title.split(':')[0]; // "Best IPTV: Guide" -> "Best IPTV"
};

/**
 * Main function to get related articles dynamically
 */
export const getDynamicRelatedArticles = (currentArticle, allArticles, limit = 5) => {
    if (!currentArticle || !allArticles) return [];

    const candidates = allArticles.filter(a => a.slug !== currentArticle.slug);

    const scoredCandidates = candidates.map(candidate => {
        let score = 0;

        // 1. Tag Overlap
        const sharedTags = candidate.tags?.filter(t => currentArticle.tags?.includes(t)) || [];
        score += sharedTags.length * SCORE.SAME_TAG;

        // 2. Category Match
        if (candidate.category === currentArticle.category) {
            score += SCORE.SAME_CATEGORY;
        }

        // 3. Pillar Strategy: Link UP to pillars
        if (candidate.type === 'pillar') {
            score += SCORE.PILLAR_BONUS;
        }

        // 4. Randomize slightly to avoid static loops (Spider Net Effect)
        score += Math.random() * 0.5;

        return { ...candidate, score, sharedTags };
    });

    // Sort by score
    const sorted = scoredCandidates.sort((a, b) => b.score - a.score);

    // Select top N
    const results = sorted.slice(0, limit);

    // Map to the format needed by components { slug, anchor, title }
    return results.map(article => ({
        slug: article.slug,
        anchor: getAnchorText(article),
        title: article.title,
        type: article.type, // Pass type for styling cues
        rel: 'dofollow'
    }));
};
